import {useMemo, useState} from 'react'
import {Navigate, useNavigate} from 'react-router-dom'
import {DateTime} from 'luxon'
import {useTranslation} from 'react-i18next'

import {
  TimelineContainer,
  MonthHeaderGrid,
  MonthHeader,
  ServicesStack,
  ServiceRow,
  PageContainer,
  GridWrapper,
  LegendBar,
  LegendChip,
} from './ReportsServicesPage.styled'
import {ServiceCard} from './ServiceCard'

import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {Button} from '~/components/general/button/Button'
import {ProcessingOverlay} from '~/components/general/processingOverlay/ProcessingOverlay'
import {
  useGetPartnerServicesReport,
  useExportServicesReportPdf,
  useExportServicesReportXlsx,
} from '~/api/partnerAdminReports'
import {
  buildServicesReportPlacements,
  type ServiceReportPlacement,
} from '~/utils/servicesReportPlacement'
import {useNavigationType} from '~/hooks/useNavigationType'

const formatYyMm = (dt: DateTime) => dt.toFormat('yyLL')

type TrackGroup = {
  track: number
  items: ServiceReportPlacement[]
  maxStackLevel: number
}

const ReportsServicesPageContent = () => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const [selectedMonth, setSelectedMonth] = useState<DateTime>(
    DateTime.now().startOf('month'),
  )

  const yymm = useMemo(() => formatYyMm(selectedMonth), [selectedMonth])

  const {data, isLoading} = useGetPartnerServicesReport(yymm, {
    enabled: true,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  })

  const {mutate: exportPdf, isPending: isExportingPdf} =
    useExportServicesReportPdf(yymm)
  const {mutate: exportXlsx, isPending: isExportingXlsx} =
    useExportServicesReportXlsx()

  const isExporting = isExportingPdf || isExportingXlsx

  const windowStart = selectedMonth.startOf('month')
  const totalDays = selectedMonth.daysInMonth

  const placedItems = useMemo(
    () =>
      buildServicesReportPlacements(
        data?.services || [],
        windowStart,
        totalDays,
      ),
    [data?.services, windowStart, totalDays],
  )

  const trackGroups = useMemo(() => {
    const groups = new Map<number, ServiceReportPlacement[]>()

    placedItems.forEach((placement) => {
      const trackItems = groups.get(placement.track) || []
      trackItems.push(placement)
      groups.set(placement.track, trackItems)
    })

    return Array.from(groups.entries())
      .sort(([trackA], [trackB]) => trackA - trackB)
      .map(
        ([track, items]): TrackGroup => ({
          track,
          items,
          maxStackLevel: Math.max(...items.map((item) => item.stackLevel), 0),
        }),
      )
  }, [placedItems])

  return (
    <PageContainer direction="column" spacing={2} flex fullWidth>
      <Container
        flex
        direction="row"
        justify="space-between"
        align="center"
        spacing={2}>
        <Container flex direction="row" spacing={2}>
          <Button onClick={() => setSelectedMonth((m) => m.minus({months: 1}))}>
            {t('Previous Month')}
          </Button>
          <Button onClick={() => setSelectedMonth((m) => m.plus({months: 1}))}>
            {t('Next Month')}
          </Button>
        </Container>
        <Container flex direction="row" spacing={2}>
          <Button onClick={() => exportPdf()} disabled={isExporting}>
            {t('Export PDF')}
          </Button>
          <Button onClick={() => exportXlsx()} disabled={isExporting}>
            {t('Export Excel')}
          </Button>
        </Container>
      </Container>

      <GridWrapper direction="column" spacing={2} fullWidth flex>
        {isLoading && (
          <Typography size="sm" weight="normal">
            {t('Loading...')}
          </Typography>
        )}
        {!isLoading && (
          <TimelineContainer direction="column" spacing={1} fullWidth>
            <MonthHeaderGrid $columns={totalDays}>
              <MonthHeader
                $colStart={1}
                $colEnd={totalDays + 1}
                justify="center"
                align="center">
                <Typography weight="bold">
                  {selectedMonth.toLocaleString({
                    month: 'long',
                    year: 'numeric',
                  })}
                </Typography>
              </MonthHeader>
            </MonthHeaderGrid>

            <ServicesStack direction="column" spacing={1}>
              {trackGroups.map(({track, items, maxStackLevel}) => (
                <ServiceRow
                  key={`track-${track}`}
                  $columns={totalDays}
                  $maxStackLevel={maxStackLevel}>
                  {items.map((placement) => (
                    <ServiceCard
                      key={placement.serviceId}
                      name={placement.name}
                      staff={placement.staff}
                      startDate={placement.startDate}
                      endDate={placement.endDate}
                      startIdx={placement.startIdx}
                      endIdx={placement.endIdx}
                      stackLevel={placement.stackLevel}
                      status={placement.status}
                      onClick={() =>
                        navigate(
                          `/app/reports/patients?serviceId=${placement.serviceId}`,
                        )
                      }
                    />
                  ))}
                </ServiceRow>
              ))}
            </ServicesStack>
          </TimelineContainer>
        )}
      </GridWrapper>
      <LegendBar flex direction="row" spacing={2} align="center">
        <Typography weight="bold">{t('Key')}:</Typography>
        <LegendChip $status="onsite" align="center">
          <Typography>{t('On site')}</Typography>
        </LegendChip>
        <LegendChip $status="scheduled" align="center">
          <Typography>{t('Scheduled')}</Typography>
        </LegendChip>
        <LegendChip $status="complete" align="center">
          <Typography>{t('Complete')}</Typography>
        </LegendChip>
      </LegendBar>
      <ProcessingOverlay isVisible={isLoading || isExporting} />
    </PageContainer>
  )
}

export const ReportsServicesPage = () => {
  const {isPractitionerOnly} = useNavigationType()
  if (isPractitionerOnly) {
    return <Navigate replace to="/app/reports/patients" />
  }
  return <ReportsServicesPageContent />
}
